import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ATTENTION_PRESETS } from "../../utils/franchiseAdminModel";

// Search + Active-only + Needs Attention presets (design §5, slice A2).
//
// "Needs Attention" is expressed as FILTER PRESETS on this one toolbar rather than as a separate
// board or screen: one mechanism, no duplicated surface, and the counts still work as a to-do
// list. Counts come from countAttention() over the active-filtered fleet, so they are computed
// client-side from the already-loaded payload -- no extra endpoint, and they always agree with
// what is on screen.
export default function FranchiseAdminToolbar(props) {
  const query = props.query;
  const onQueryChange = props.onQueryChange;
  const activeOnly = props.activeOnly;
  const onActiveOnlyChange = props.onActiveOnlyChange;
  const presetIds = props.presetIds || [];
  const onTogglePreset = props.onTogglePreset;
  const onClear = props.onClear;
  const counts = props.counts || {};
  const summary = props.summary;

  const hasFilters = query.trim() !== "" || presetIds.length > 0;

  const presetChips = ATTENTION_PRESETS.map((preset) => {
    const selected = presetIds.includes(preset.id);
    const count = counts[preset.id] ?? 0;
    return (
      <Chip
        key={preset.id}
        label={`${preset.label} · ${count}`}
        size="small"
        clickable
        onClick={() => onTogglePreset(preset.id)}
        color={selected ? (preset.severity === "warn" ? "warning" : "primary") : "default"}
        variant={selected ? "filled" : "outlined"}
        // A preset that matches nothing is left visible but de-emphasised, so the set of things
        // worth checking stays stable rather than appearing and disappearing between loads.
        sx={count === 0 && !selected ? { color: "text.disabled", borderColor: "divider" } : undefined}
      />
    );
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search franchise, com code, or facility"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          sx={{ minWidth: 320, flexGrow: 1, maxWidth: 480 }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={activeOnly}
              onChange={(event) => onActiveOnlyChange(event.target.checked)}
            />
          }
          label={<Typography variant="body2">Active only</Typography>}
        />
        <Box sx={{ flexGrow: 1 }} />
        {summary && (
          <Typography variant="body2" color="text.secondary">
            <strong>{summary.franchiseCount}</strong> franchises ·{" "}
            <strong>{summary.facilityCount}</strong> facilities
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
          Needs attention:
        </Typography>
        {presetChips}
        {hasFilters && (
          <Button size="small" onClick={onClear} sx={{ textTransform: "none" }}>
            Clear filters
          </Button>
        )}
      </Box>
    </Paper>
  );
}
